import React from 'react';
import { X as CloseIcon } from 'lucide-react';

import useAuthService from 'src/shared/hooks/useAuthService';

import { useAppSelector, useAppDispatch } from 'src/store';
import { getNewProjectUserFormState } from 'src/app/admin/views/user-management/state/userManagementSelector';
import type { NewProjectUserFormState } from 'src/app/admin/views/user-management/state/userManagementSlice';
import { reset } from 'src/app/admin/views/user-management/state/userManagementSlice';

import { SecondaryButton } from 'src/shared/components/button/Button';
import { IconButton } from 'src/shared/components/ui/icon-button';
import SelectExistingUser from './components/select-existing-user/SelectExistingUser';
import styles from './UserDetailsForm.module.scss';
import UserTypeForm from './components/user-type-form/UserTypeForm';
import CreateNewUserForm from './components/create-new-user-form/CreateNewUserForm';

/**
 * Whether to show create-new-user fields vs existing-user search.
 * Super admins default to existing-user search; non–super admins default to create (matches prior `!isCurrentUserSuperAdmin`).
 * When step 2 unmounts this component and Back remounts it, Redux still holds the draft — show create again so Back returns to the same sub-step.
 */
const computeInitialShowCreateUserForm = (formState: NewProjectUserFormState['formState'], isCurrentUserSuperAdmin: boolean) => {
    if (formState.selectedUser) {
        return false;
    }
    if (!isCurrentUserSuperAdmin) {
        return true;
    }
    return Boolean(formState.firstName?.trim() || formState.lastName?.trim() || formState.email?.trim());
};

/**
 * Add-user step 1: pick internal vs external (when allowed), link existing user or create new.
 * Local `shouldShowCreateUserForm` only toggles the create form overlay; Redux holds real form data.
 */
const UserDetails = () => {
    const { isCurrentUserSuperAdmin } = useAuthService();
    const formState = useAppSelector(getNewProjectUserFormState);
    const [shouldShowCreateUserForm, setShouldShowCreateUserForm] = React.useState(() =>
        computeInitialShowCreateUserForm(formState, isCurrentUserSuperAdmin)
    );
    const dispatch = useAppDispatch();

    return (
        <div className={styles.userDetailsForm} data-testid='user-management-add-user-step-one'>
            {isCurrentUserSuperAdmin && <UserTypeForm />}

            {!shouldShowCreateUserForm && (
                <>
                    <SelectExistingUser />
                    {!formState.selectedUser && (
                        <>
                            <div>or</div>
                            <SecondaryButton
                                onClick={() => setShouldShowCreateUserForm(true)}
                                data-testid='user-management-show-create-new-user-form'
                            >
                                Create new user
                            </SecondaryButton>
                        </>
                    )}
                </>
            )}
            {shouldShowCreateUserForm && (
                <div className={styles.createUserFormWrapper}>
                    <div className={styles.close}>
                        <IconButton
                            aria-label='close'
                            onClick={() => {
                                setShouldShowCreateUserForm(false);
                                dispatch(reset());
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>
                    <CreateNewUserForm />
                </div>
            )}
        </div>
    );
};

export default UserDetails;
